import type { LocaleDefinition } from "@packsize/localization";
import type { ReactNode } from "react";
import type { z } from "zod";
import { LocalizationProvider, createLocaleSchema, useLocale } from "@packsize/localization";
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import { LocalizationProvider as DatePickersLocalization } from "@mui/x-date-pickers-pro";

import { storagePrefix } from "./environment";

const localeSchema = createLocaleSchema([
	"cs",
	"da-DK",
	"de-DE",
	"en",
	"es-419",
	"es-ES",
	"fl",
	"fr-CA",
	"fr-FR",
	"hu-HU",
	"it-IT",
	"nl-NL",
	"pl-PL",
	"pt",
	"ro-RO",
	"sk-SK",
	"sv-SE",
] as const);
type SupportedLocales = z.infer<typeof localeSchema>;

const locales: LocaleDefinition<SupportedLocales>[] = [
	{ key: "cs", label: "Czech", loader: () => import("./translations/compiled/cs.json") },
	{ key: "da-DK", label: "Dansk", loader: () => import("./translations/compiled/da.json") },
	{ key: "de-DE", label: "Deutsch", loader: () => import("./translations/compiled/de.json") },
	{ key: "en", label: "English", loader: () => import("./translations/compiled/en.json") },
	{ key: "es-419", label: "Español (Americas)", loader: () => import("./translations/compiled/es_419.json") },
	{ key: "es-ES", label: "Español (Europe)", loader: () => import("./translations/compiled/es.json") },
	{ key: "fl", label: "Flemish", loader: () => import("./translations/compiled/fl.json") },
	{ key: "fr-CA", label: "Français Canadien", loader: () => import("./translations/compiled/fr_CA.json") },
	{ key: "fr-FR", label: "Français", loader: () => import("./translations/compiled/fr.json") },
	{ key: "hu-HU", label: "Magyar", loader: () => import("./translations/compiled/hu.json") },
	{ key: "it-IT", label: "Italiano", loader: () => import("./translations/compiled/it.json") },
	{ key: "nl-NL", label: "Nederlands", loader: () => import("./translations/compiled/nl.json") },
	{ key: "pl-PL", label: "Polski", loader: () => import("./translations/compiled/pl.json") },
	{ key: "pt", label: "Portuguese", loader: () => import("./translations/compiled/pt.json") },
	{ key: "ro-RO", label: "Română", loader: () => import("./translations/compiled/ro.json") },
	{ key: "sk-SK", label: "Slovensky", loader: () => import("./translations/compiled/sk.json") },
	{ key: "sv-SE", label: "Svenska", loader: () => import("./translations/compiled/sv.json") },
];

interface LocaleProps {
	readonly children: ReactNode;
}

const DatePickerLocale = ({ children }: LocaleProps) => {
	const { currentLocale } = useLocale();

	return (
		<DatePickersLocalization adapterLocale={currentLocale.locale} dateAdapter={AdapterLuxon}>
			{children}
		</DatePickersLocalization>
	);
};

export const Locale = ({ children }: LocaleProps) => {
	return (
		<LocalizationProvider
			locales={locales}
			defaultLocale="en"
			localeSchema={localeSchema}
			storagePrefix={storagePrefix}
		>
			<DatePickerLocale>{children}</DatePickerLocale>
		</LocalizationProvider>
	);
};
